// GalleryPage.jsx — Public gallery
import React, { useState, useEffect } from "react";
import { X, ZoomIn, Image as ImageIcon, RefreshCw } from "lucide-react";
import { AnimatedSection, SectionLabel, Seo } from "../components/ui/index";
import { galleryAPI, cmsAPI } from "../services/api";
import { getImageUrl } from "../utils/image";

const CATS = ["All","Products","Services","Team","Events","Factory","General"];

function Lightbox({ item, onClose }) {
  if (!item) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={onClose}>
      <div className="max-w-3xl w-full" onClick={e=>e.stopPropagation()}>
        <img src={getImageUrl(item.imageUrl)} alt={item.altText||item.title} className="w-full max-h-[70vh] object-contain rounded-2xl" />
        <div className="bg-white/10 backdrop-blur-sm rounded-2xl mt-3 p-4 flex items-center justify-between">
          <div><p className="text-white font-semibold">{item.title}</p><span className="text-white/60 text-xs">{item.category}</span></div>
          <button onClick={onClose} className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center text-white hover:bg-white/30"><X className="w-4 h-4" /></button>
        </div>
      </div>
    </div>
  );
}

export function GalleryPublicPage() {
  const [cms, setCms] = useState({});
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("All");
  const [viewItem, setViewItem] = useState(null);

  useEffect(() => {
    Promise.all([
      galleryAPI.getAll().then(r=>setItems(r.data.items||[])),
      cmsAPI.getPage("gallery").then(r=>setCms(r.data.content||{})),
    ]).catch(()=>{}).finally(()=>setLoading(false));
  }, []);

  const g = (s, k, fb) => cms?.[s]?.[k]?.value || fb;

  const filtered = activeCategory==="All" ? items : items.filter(i=>i.category===activeCategory);

  return (
    <div className="pt-20">
      <Seo page="gallery" defaultTitle="Gallery — Sava Fresh Trading FZE" />
      <section className="bg-gradient-to-br from-brand-pale via-white to-accent-tealLight py-16">
        <div className="container-custom">
          <AnimatedSection className="max-w-2xl">
            <SectionLabel text="Gallery" />
            <h1 className="section-heading mb-4">{g("hero", "heading", "Our Visual Story")}</h1>
            <div className="text-gray-600 leading-relaxed text-lg rich-content" dangerouslySetInnerHTML={{ __html: g("hero", "description", "Explore our products, facilities, and global operations through our photo gallery.") }} />
          </AnimatedSection>
        </div>
      </section>

      <div className="sticky top-16 md:top-20 z-40 bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-soft py-3">
        <div className="container-custom flex flex-wrap gap-2">
          {CATS.map(cat=>(
            <button key={cat} onClick={()=>setActiveCategory(cat)}
              className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-all ${activeCategory===cat?"bg-brand-primary text-white shadow-soft":"bg-gray-100 text-gray-600 hover:bg-brand-pale hover:text-brand-primary"}`}>{cat}</button>
          ))}
          <span className="ml-auto text-xs text-gray-400 self-center">{filtered.length} photos</span>
        </div>
      </div>

      <section className="section-pad bg-gray-section">
        <div className="container-custom">
          {loading ? <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">{[...Array(8)].map((_,i)=><div key={i} className="bg-white rounded-2xl h-44 animate-pulse" />)}</div>
          : filtered.length===0 ? (
            <div className="text-center py-20"><ImageIcon className="w-16 h-16 text-gray-200 mx-auto mb-3" /><p className="text-gray-400">No images in this category yet.</p></div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {filtered.map((item,i)=>(
                <AnimatedSection key={item.id} delay={i*40}>
                  <div className="group relative rounded-2xl overflow-hidden cursor-pointer shadow-card hover:shadow-hover transition-all duration-300" style={{minHeight:"160px"}} onClick={()=>setViewItem(item)}>
                    <img src={getImageUrl(item.imageUrl)} alt={item.altText||item.title} className="w-full h-44 object-cover group-hover:scale-110 transition-transform duration-700" onError={e=>e.target.parentElement.classList.add("bg-brand-pale","flex","items-center","justify-center")} />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    <div className="absolute bottom-0 left-0 right-0 p-3 translate-y-4 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-300">
                      <span className="badge-brand mb-1 text-xs inline-block">{item.category}</span>
                      <h4 className="font-semibold text-white text-xs">{item.title}</h4>
                    </div>
                    <div className="absolute top-2.5 right-2.5 w-8 h-8 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <ZoomIn className="w-4 h-4 text-white" />
                    </div>
                  </div>
                </AnimatedSection>
              ))}
            </div>
          )}
        </div>
      </section>

      {viewItem && <Lightbox item={viewItem} onClose={()=>setViewItem(null)} />}
    </div>
  );
}
